import React, { useState, useEffect } from 'react';
import { Search, Filter, Loader2, Target, FileText, Sparkles } from 'lucide-react';
import toast from 'react-hot-toast';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';

const Jobsite25_111Search = () => {
  const { user, isAuthenticated } = useAuth();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [summary, setSummary] = useState('');
  const [summaryTimer, setSummaryTimer] = useState(100);
  const [filters, setFilters] = useState({
    fileSource: 'all',
    topK: 10
  });

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Timer effect for summary generation
  useEffect(() => {
    let interval;
    if (summaryLoading && summaryTimer > 0) {
      interval = setInterval(() => {
        setSummaryTimer(prev => prev - 1);
      }, 1000);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [summaryLoading, summaryTimer]);

  // Reset timer when summary loading starts
  useEffect(() => {
    if (summaryLoading) {
      setSummaryTimer(100);
    }
  }, [summaryLoading]);




  // Helper function to generate summary for results
  const generateSummaryForResults = async (query, results) => {
    setSummaryLoading(true);
    setSummaryTimer(100);
    
    try {
      const response = await fetch('/api/25-111-search/summary', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          query: query.trim(),
          results: results
        }),
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setSummary(data.summary);
          toast.success(`Generated summary for ${data.files_processed} files`);
        } else {
          toast.error(data.error || 'Summary generation failed');
        }
      } else {
        throw new Error('Summary request failed');
      }
    } catch (error) {
      console.error('Auto-summary error:', error);
      toast.error('Summary generation failed.');
    } finally {
      setSummaryLoading(false);
    }
  };

  // Group results by file path to avoid duplicates
  const groupedResults = results.reduce((acc, result) => {
    const filePath = result.file_path;
    if (!acc[filePath]) {
      acc[filePath] = [];
    }
    acc[filePath].push(result);
    return acc;
  }, {});

  // Generate SharePoint URLs for both file and folder access
  const generateSharePointUrls = (filePath) => {
    try {
      // Hardcode the correct site folder name
      const siteFolder = '25-111 Ford OAC GA Tooling';
      const siteNameWithoutSpaces = siteFolder.replace(/\s+/g, '');
      
      // Parse the file path (the filePath now contains the subfolder structure)
      const pathParts = filePath.split('\\');
      
      // Get the full file path (including filename)
      const fullFilePath = pathParts.join('\\');
      
      // Get the folder path (remove the filename, keep only the folder structure)
      const folderPath = pathParts.slice(0, -1).join('\\');
      
      // Encode special characters for URL (comprehensive encoding)
      const encodePath = (path) => {
        return path
          .replace(/\s/g, '%20')           // Space → %20
          .replace(/-/g, '%2D')             // Hyphen → %2D
          .replace(/\\/g, '%2F')            // Backslash → %2F
          .replace(/&/g, '%26')             // Ampersand → %26
          .replace(/\./g, '%2E')            // Period → %2E
          .replace(/\//g, '%2F')            // Forward slash → %2F
          .replace(/\?/g, '%3F')            // Question mark → %3F
          .replace(/=/g, '%3D')             // Equals → %3D
          .replace(/\+/g, '%2B')            // Plus → %2B
          .replace(/,/g, '%2C')             // Comma → %2C
          .replace(/:/g, '%3A')             // Colon → %3A
          .replace(/;/g, '%3B')             // Semicolon → %3B
          .replace(/"/g, '%22')             // Quote → %22
          .replace(/'/g, '%27')             // Apostrophe → %27
          .replace(/</g, '%3C')             // Less than → %3C
          .replace(/>/g, '%3E')             // Greater than → %3E
          .replace(/\[|\]/g, '%5B%5D');     // Brackets → %5B%5D
      };
      
      // Generate folder URL
      let folderUrl = `https://fmsylvan2.sharepoint.com/sites/${siteNameWithoutSpaces}/Shared%20Documents/Forms/AllItems.aspx?id=%2Fsites%2F${siteNameWithoutSpaces}%2FShared%20Documents`;
      if (folderPath) {
        const encodedFolderPath = encodePath(folderPath);
        folderUrl += `%2F${encodedFolderPath}`;
      }
      
      // Generate direct file URL (if there's a filename)
      let fileUrl = null;
      if (fullFilePath) {
        const encodedFilePath = encodePath(fullFilePath);
        const encodedParentPath = encodePath(folderPath || '');
        fileUrl = `https://fmsylvan2.sharepoint.com/sites/${siteNameWithoutSpaces}/Shared%20Documents/Forms/AllItems.aspx?id=%2Fsites%2F${siteNameWithoutSpaces}%2FShared%20Documents%2F${encodedFilePath}&parent=%2Fsites%2F${siteNameWithoutSpaces}%2FShared%20Documents%2F${encodedParentPath}`;
      }
      
      return {
        folder: folderUrl,
        file: fileUrl
      };
    } catch (error) {
      console.error('Error generating SharePoint URLs:', error);
      return { folder: null, file: null };
    }
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!query.trim()) {
      toast.error('Please enter a search query');
      return;
    }

    setLoading(true);
    setSummary(''); // Clear previous summary
    setSummaryLoading(true); // Start summary loading immediately
    
    try {
      const response = await fetch('/api/25-111-search/search', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          query: query.trim(),
          top_k: filters.topK,
          file_source_filter: filters.fileSource === 'all' ? undefined : filters.fileSource
        }),
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setResults(data.results);
          toast.success(`Found ${data.total_results} results`);
          
          // Automatically generate summary after successful search
          await generateSummaryAutomatically(data.results);
        } else {
          toast.error(data.error || 'Search failed');
          setSummaryLoading(false);
        }
      } else {
        throw new Error('Search request failed');
      }
    } catch (error) {
      console.error('Search error:', error);
      toast.error('Search failed. Please try again.');
      setSummaryLoading(false);
    } finally {
      setLoading(false);
    }
  };

  const generateSummaryAutomatically = async (searchResults) => {
    if (!searchResults.length) {
      setSummaryLoading(false);
      return;
    }

    try {
      const response = await fetch('/api/25-111-search/summary', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          query: query.trim(),
          results: searchResults
        }),
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setSummary(data.summary);
          toast.success(`Generated summary for ${data.files_processed} files`);
        } else {
          toast.error(data.error || 'Summary generation failed');
        }
      } else {
        throw new Error('Summary request failed');
      }
    } catch (error) {
      console.error('Summary error:', error);
      toast.error('Summary generation failed. Please try again.');
    } finally {
      setSummaryLoading(false);
    }
  };

  const handleGenerateSummary = async () => {
    if (!results.length) {
      toast.error('No search results to summarize');
      return;
    }

    setSummaryLoading(true);
    try {
      const response = await fetch('/api/25-111-search/summary', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          query: query.trim(),
          results: results
        }),
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setSummary(data.summary);
          toast.success(`Generated summary for ${data.files_processed} files`);
        } else {
          toast.error(data.error || 'Summary generation failed');
        }
      } else {
        throw new Error('Summary request failed');
      }
    } catch (error) {
      console.error('Summary error:', error);
      toast.error('Summary generation failed. Please try again.');
    } finally {
      setSummaryLoading(false);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const clearFilters = () => {
    setFilters({
      fileSource: 'all',
      topK: 10
    });
  };

  const formatScore = (score) => {
    return (score * 100).toFixed(1) + '%';
  };

  const getFileTypeIcon = (fileType) => {
    switch (fileType?.toLowerCase()) {
      case '.pdf':
        return '📄';
      case '.docx':
      case '.doc':
        return '📝';
      case '.xlsx':
      case '.xls':
        return '📊';
      case '.txt':
        return '📃';
      default:
        return '📁';
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center mb-4">
          <div className="p-3 bg-sylvan-100 rounded-lg mr-4">
            <Search className="h-8 w-8 text-sylvan-600" />
          </div>
          <div>
            <div className="flex items-center space-x-3 mb-2">
              <h1 className="text-3xl font-bold text-gray-900">SharePoint Analyst</h1>
            </div>
            <p className="text-gray-600 mt-1">
              Advanced SharePoint search and analysis for construction project documents and data
            </p>
            <div className="mt-3 bg-blue-50 border border-blue-200 rounded-lg p-3">
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-blue-400 mt-0.5" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-blue-800">
                    Demo Version - Project 25-111
                  </h3>
                  <div className="mt-1 text-sm text-blue-700">
                    <p>This SharePoint Analyst is currently configured for Project 25-111 (Ford GA Tooling) as a demonstration. <strong>Will extend to all projects soon!</strong></p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>


      {/* Search Form */}
      <div className="bg-white rounded-lg border border-gray-200 p-6 mb-8">
        <form onSubmit={handleSearch} className="space-y-4">
          <div>
            <label htmlFor="query" className="block text-sm font-medium text-gray-700 mb-2">
              Search Query
            </label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                id="query"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Enter your search query (e.g., 'concrete foundation specifications', 'electrical conduit installation')"
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sylvan-500 focus:border-sylvan-500"
                disabled={loading}
              />
            </div>
          </div>

          {/* Filters */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label htmlFor="fileSource" className="block text-sm font-medium text-gray-700 mb-2">
                File Source
              </label>
              <select
                id="fileSource"
                value={filters.fileSource}
                onChange={(e) => handleFilterChange('fileSource', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sylvan-500 focus:border-sylvan-500"
              >
                <option value="all">All sources</option>
                <option value="sharepoint">SharePoint</option>
                {/* Future data sources can be added here */}
              </select>
            </div>
            <div>
              <label htmlFor="topK" className="block text-sm font-medium text-gray-700 mb-2">
                Number of Results
              </label>
              <select
                id="topK"
                value={filters.topK}
                onChange={(e) => handleFilterChange('topK', parseInt(e.target.value))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sylvan-500 focus:border-sylvan-500"
              >
                <option value={5}>5 results</option>
                <option value={10}>10 results</option>
                <option value={20}>20 results</option>
                <option value={50}>50 results</option>
              </select>
            </div>
          </div>

          <div className="flex items-center justify-between">
            <button
              type="button"
              onClick={clearFilters}
              className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-sylvan-500"
            >
              <Filter className="h-4 w-4 mr-2" />
              Clear Filters
            </button>
            <button
              type="submit"
              disabled={loading || !query.trim()}
              className="inline-flex items-center px-6 py-3 border border-transparent rounded-lg text-sm font-medium text-white bg-sylvan-600 hover:bg-sylvan-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-sylvan-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Searching...
                </>
              ) : (
                <>
                  <Search className="h-4 w-4 mr-2" />
                  Search
                </>
              )}
            </button>
          </div>
        </form>
      </div>

      {/* Summary Section */}
      {results.length > 0 && (
        <div className="bg-white rounded-lg border border-gray-200 p-6 mb-8">
          <div className="mb-4">
            <h3 className="text-lg font-medium text-gray-900 flex items-center">
              <Sparkles className="h-5 w-5 mr-2 text-purple-600" />
              AI Summary
            </h3>
            <p className="text-sm text-gray-600 mt-1">
              {summaryLoading ? 'Generating AI-powered summary...' : 'AI-powered summary of the top search results'}
            </p>
          </div>
          
          {/* Loading State */}
          {summaryLoading && (
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-6 text-center">
              <Loader2 className="h-8 w-8 text-purple-600 mx-auto mb-3 animate-spin" />
              <p className="text-purple-700 font-medium">Generating Summary...</p>
              <p className="text-purple-600 text-sm mt-1">
                Analyzing {Object.keys(groupedResults).length} files for "{query}"
              </p>
              
              {/* Countdown Timer */}
              <div className="mt-4">
                <div className="flex items-center justify-center space-x-2 mb-2">
                  <span className="text-purple-700 text-sm font-medium">Time remaining:</span>
                  <span className={`text-lg font-bold ${summaryTimer <= 20 ? 'text-red-600' : 'text-purple-600'}`}>
                    {Math.floor(summaryTimer / 60)}:{(summaryTimer % 60).toString().padStart(2, '0')}
                  </span>
                </div>
                
                {/* Progress Bar */}
                <div className="w-full bg-purple-200 rounded-full h-2 mb-2">
                  <div 
                    className={`h-2 rounded-full transition-all duration-1000 ease-linear ${
                      summaryTimer <= 20 ? 'bg-red-500' : 'bg-purple-600'
                    }`}
                    style={{ width: `${((100 - summaryTimer) / 100) * 100}%` }}
                  ></div>
                </div>
                
                <p className="text-purple-500 text-xs">
                  Estimated completion in {summaryTimer} seconds
                </p>
                
                {/* Warning for low time */}
                {summaryTimer <= 20 && (
                  <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded-md">
                    <p className="text-red-700 text-xs font-medium">
                      ⚠️ Summary generation is taking longer than expected. Please be patient.
                    </p>
                  </div>
                )}
              </div>
              
              <p className="text-purple-500 text-xs mt-3">
                You can browse the search results below while the summary is being generated
              </p>
            </div>
          )}
          
          {/* Summary Content */}
          {summary && !summaryLoading && (
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
              <h4 className="text-sm font-medium text-purple-900 mb-3">Summary for: "{query}"</h4>
              <div className="prose prose-sm max-w-none">
                <p className="text-gray-700 leading-relaxed whitespace-pre-wrap">{summary}</p>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Search Results */}
      {results.length > 0 && (
        <div className="bg-white rounded-lg border border-gray-200">
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-medium text-gray-900">
                  Search Results ({results.length} matches across {Object.keys(groupedResults).length} files)
                </h3>
                <p className="text-sm text-gray-600 mt-1">
                  Query: "{query}"
                </p>
              </div>
              {summaryLoading && (
                <div className="flex items-center text-purple-600">
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  <span className="text-sm font-medium">
                    Generating Summary... ({Math.floor(summaryTimer / 60)}:{(summaryTimer % 60).toString().padStart(2, '0')})
                  </span>
                </div>
              )}
            </div>
          </div>
          <div className="divide-y divide-gray-200">
            {Object.entries(groupedResults).map(([filePath, fileResults]) => (
              <div key={filePath} className="p-6 hover:bg-gray-50 transition-colors">
                <div className="flex items-start mb-4">
                  <span className="text-2xl mr-3">{getFileTypeIcon(fileResults[0].file_type)}</span>
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="text-sm font-medium text-gray-900">
                        {filePath}
                      </h4>
                      {(() => {
                        const urls = generateSharePointUrls(filePath);
                        return (
                          <div className="flex space-x-2">
                            {urls.file && (
                              <a
                                href={urls.file}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center px-3 py-1.5 border border-green-300 rounded-md text-xs font-medium text-green-700 bg-green-50 hover:bg-green-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 transition-colors"
                                title="Open file directly in SharePoint"
                              >
                                <svg className="h-3 w-3 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                                </svg>
                                Open File
                              </a>
                            )}
                          </div>
                        );
                      })()}
                    </div>
                    <div className="flex items-center space-x-4 text-xs text-gray-500 mb-3">
                      <span>Type: {fileResults[0].file_type}</span>
                      <span>{fileResults.length} matching chunks</span>
                      <span className="flex items-center">
                        <Target className="h-3 w-3 mr-1" />
                        Best Score: {formatScore(Math.max(...fileResults.map(r => r.score)))}
                      </span>
                    </div>
                    
                    {/* Content snippets from all chunks */}
                    <div className="space-y-3">
                      {fileResults.map((result, index) => (
                        <div key={result.id} className="bg-gray-50 rounded-lg p-3">
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-xs text-gray-500 font-medium">
                              Chunk {result.chunk_index}
                            </span>
                            <span className="text-xs text-gray-500">
                              Score: {formatScore(result.score)}
                            </span>
                          </div>
                          <p className="text-sm text-gray-700 leading-relaxed">
                            {result.text_preview || result.text || 'No content available'}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* No Results */}
      {results.length === 0 && !loading && query && (
        <div className="text-center py-12">
          <Search className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No results found</h3>
          <p className="text-gray-600">
            Try adjusting your search query or filters to find what you're looking for.
          </p>
        </div>
      )}
    </div>
  );
};

export default Jobsite25_111Search;
